import {
  type ButtonHTMLAttributes,
  Children,
  forwardRef,
  isValidElement,
  type ReactNode,
} from "react";
import { cn } from "../lib/cn.ts";
import { Spinner } from "./spinner.tsx";

/**
 * Botão.
 *
 * Ícone à esquerda, rótulo à direita, sempre em linha. O SVG do Tailwind
 * Preflight é `display: block`; sem `flex-row` o ícone empilhava em cima do
 * texto — o "Adicionar link" virava um tijolo. Aqui o eixo é travado.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "subtle";
export type ButtonSize = "sm" | "md" | "lg" | "icon";

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-600 text-white shadow-xs hover:bg-[var(--dream-brand-hover)] active:bg-[var(--dream-brand-active)] " +
    "disabled:bg-brand-50 disabled:text-brand-500 disabled:shadow-none",
  secondary:
    "bg-[var(--color-surface)] text-[var(--color-ink)] border border-[var(--color-line-strong)] " +
    "hover:bg-[var(--color-surface-muted)] hover:border-[var(--color-ink-subtle)] " +
    "active:bg-[var(--color-surface-subtle)] disabled:bg-[var(--color-surface-muted)]",
  ghost:
    "bg-transparent text-[var(--color-ink-secondary)] hover:bg-[var(--color-surface-muted)] " +
    "hover:text-[var(--color-ink)] active:bg-[var(--color-surface-inset)]",
  danger:
    "bg-[#F44A4A] text-white shadow-xs hover:bg-[#AB3434] active:bg-[#AB3434] disabled:bg-[#F99696]",
  subtle:
    "bg-brand-50 text-[var(--dream-info-fg)] hover:bg-brand-100 active:bg-brand-100 " +
    "disabled:bg-[var(--color-surface-muted)] disabled:text-[var(--color-ink-subtle)]",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-2.5 text-[0.75rem] gap-1.5 rounded-[var(--radius-md)]",
  md: "h-10 px-4 text-[0.875rem] gap-2 rounded-[var(--radius-md)]",
  lg: "h-11 px-5 text-[16px] gap-2 rounded-lg",
  icon: "h-10 w-10 shrink-0 rounded-[var(--radius-md)]",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

function ButtonLabel({ children }: { children: ReactNode }) {
  return (
    <>
      {Children.map(children, (child) => {
        if (typeof child === "string" || typeof child === "number") {
          return <span className="min-w-0 truncate">{child}</span>;
        }
        if (isValidElement(child)) return child;
        return child;
      })}
    </>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    iconLeft,
    iconRight,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  const isDisabled = disabled === true || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex flex-row flex-nowrap items-center justify-center font-semibold tracking-[0] select-none",
        "transition-[background-color,border-color,color,box-shadow,transform]",
        "duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
        "enabled:active:scale-[0.98]",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
        "disabled:cursor-not-allowed disabled:opacity-70",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner className={size === "sm" ? "size-3.5" : "size-4"} />
      ) : (
        iconLeft !== undefined && <span className="shrink-0">{iconLeft}</span>
      )}

      {children !== undefined &&
        !loading &&
        (size === "icon" ? children : <ButtonLabel>{children}</ButtonLabel>)}

      {!loading && iconRight !== undefined && <span className="shrink-0">{iconRight}</span>}
    </button>
  );
});
